import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExternalLinkAlt,
  faTrashAlt,
  faSyncAlt,
  faList,
  faInfoCircle,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

// Interface for manifest files
interface ManifestFile {
  name: string;
  path: string;
}

interface GeneratedManifestsProps {
  manifestFiles: ManifestFile[];
  loadingManifestFiles: boolean;
  onRefresh: () => void;
  onDelete: (fileName: string) => void;
  apiBaseUrl: string;
  limitDisplay?: number;
  onViewAll?: () => void;
  onGenerate: () => void;
  isGenerating: boolean;
}

const GeneratedManifests: React.FC<GeneratedManifestsProps> = ({
  manifestFiles,
  loadingManifestFiles,
  onRefresh,
  onDelete,
  apiBaseUrl,
  limitDisplay = 6,
  onViewAll,
  onGenerate,
  isGenerating
}) => {
  // Get limited manifest files for the main view
  const limitedManifestFiles = manifestFiles.slice(0, limitDisplay);

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          Web Flasher Manifests
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip className="text-start" style={{ maxWidth: '300px', textAlign: 'left' }}>
                Web Flasher Manifests are auto-generated from the releases to configure all the necessary binaries to be flashed by the Web Flasher.
              </Tooltip>
            }
          >
            <span>
              <FontAwesomeIcon 
                icon={faInfoCircle} 
                className="ms-2 text-muted" 
                style={{ fontSize: '1rem' }}
              />
            </span>
          </OverlayTrigger>
        </h5>
        <div>
          <button 
            className="btn btn-sm btn-outline-success me-2" 
            onClick={onGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Generating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSync} className="me-1" /> Generate Manifests
              </>
            )}
          </button>
          <button 
            className="btn btn-sm btn-outline-primary me-2" 
            onClick={onViewAll}
            disabled={loadingManifestFiles}
          >
            <FontAwesomeIcon icon={faList} className="me-1" /> View All
          </button>
          <button 
            className="btn btn-sm btn-outline-secondary" 
            onClick={onRefresh}
            disabled={loadingManifestFiles}
          >
            {loadingManifestFiles ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Refreshing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSyncAlt} className="me-1" /> Refresh
              </>
            )}
          </button>
        </div>
      </div>
      <div className="card-body">
        {loadingManifestFiles ? (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-2">Loading manifest files...</span>
          </div>
        ) : manifestFiles.length > 0 ? (
          <>
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Type</th>
                    <th>Model</th>
                    <th>Hardware</th>
                    <th>Version</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {limitedManifestFiles.map((file, index) => {
                    // Determine if it's a factory manifest from filename
                    const isFactory = file.name.startsWith('factory-');
                    
                    // Extract model name from filename pattern
                    // For normal files: Fluora_Mini-v1.5.0.json
                    // For factory files: factory-Fluora_Mini-v1.5.0.json
                    let modelMatch;
                    if (isFactory) {
                      modelMatch = file.name.match(/factory-(.+?)-v/);
                    } else {
                      modelMatch = file.name.match(/^(.+?)-v/);
                    }
                    const model = modelMatch ? modelMatch[1].replace(/_/g, ' ').split('-')[0].trim() : 'Unknown';
                    
                    // Extract version from filename pattern 
                    const versionMatch = file.name.match(/[-_]v(\d+\.\d+\.\d+)\.json$/);
                    const version = versionMatch ? versionMatch[1] : 'Unknown';
                    
                    // Extract hardware revision (if exists)
                    const hardwareMatch = file.name.match(/rev(\d+)/i);
                    const hardware = hardwareMatch ? `Rev ${hardwareMatch[1]}` : '-';
                    
                    return (
                      <tr key={index}>
                        <td>{file.name}</td>
                        <td>
                          <span className={`badge ${isFactory ? 'bg-warning text-dark' : 'bg-success'}`}>
                            {isFactory ? 'Factory' : 'Release'}
                          </span>
                        </td>
                        <td>
                        <span className={`badge ${
                            model.includes('Fluora Mini') 
                              ? 'bg-primary' 
                              : model.includes('Fluora Floor') 
                                ? 'bg-info'
                                : model.includes('Monos')
                                  ? 'bg-warning text-dark'
                                  : 'bg-warning text-dark'
                          }`}>
                            {model}
                          </span>
                        </td>
                        <td>{hardware}</td>
                        <td>v{version}</td>
                        <td>
                          <a 
                            href={`${apiBaseUrl}${file.path}`} 
                            className="btn btn-sm btn-outline-primary me-2"
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open"
                          >
                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                          </a>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => onDelete(file.name)}
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {manifestFiles.length > limitDisplay && (
              <div className="text-center mt-3">
                <small className="text-muted">
                  Showing {limitDisplay} of {manifestFiles.length} manifest files. 
                  <button 
                    className="btn btn-link btn-sm p-0 ms-1"
                    onClick={onViewAll}
                  >
                    View all manifests
                  </button>
                </small>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-3">
            <p className="text-muted mb-0">No manifest files available.</p>
            <small>
              Manifests are auto-generated from the releases.
            </small>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneratedManifests;